import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './SaasPlatform.css';

const LIFECYCLE_STEPS = [
  {
    step: 1,
    id: 'step-plans',
    badge: 'Step 1',
    title: 'Commercial Plans & Quotas (योजना और सीमाएं तय करना)',
    shortDesc: 'Define subscription tiers, maximum doctor/staff accounts, monthly registration quotas, and feature flags.',
    icon: '💎',
    route: '/saas/plans',
    buttonText: 'Open Plans & Subscriptions',
    hindiSummary: 'सबसे पहले प्लान बनाएँ या मौजूदा प्लान (Starter, Professional, Enterprise) में यूज़र लिमिट और मासिक रजिस्ट्रेशन लिमिट तय करें।',
    keyParameters: [
      { name: 'MaxUsers', desc: 'Maximum employees/pathologists/billing staff permitted for this tenant.', defaultVal: '25 Users' },
      { name: 'MaxRegistrationsPerMonth', desc: 'Monthly limit on total patient billings/work orders.', defaultVal: '1,000 / mo' },
      { name: 'IncludedCredits', desc: 'Preloaded wallet tokens for automated SMS, WhatsApp, and API calls.', defaultVal: '1,000 Credits' },
      { name: 'FeaturesJson', desc: 'Module flags (Pathology, Radiology, Microbiology, Outsource, B2B Portal).', defaultVal: 'Full Suite' }
    ]
  },
  {
    step: 2,
    id: 'step-onboarding',
    badge: 'Step 2',
    title: 'Tenant Provisioning & Setup (नया टेनेंट और डेटाबेस ऑनबोर्डिंग)',
    shortDesc: 'Register diagnostic organization, allocate database connection, assign subdomain, and seed schema.',
    icon: '🚀',
    route: '/saas/onboarding',
    buttonText: 'Open Tenant Onboarding Wizard',
    hindiSummary: 'क्लाइंट का नाम, सब-डोमेन और डेटाबेस (Separate Database) चुनकर 1-क्लिक में नया टेनेंट तैयार करें।',
    keyParameters: [
      { name: 'TenantCode', desc: 'Unique uppercase identifier (e.g. ALPHA, APEX_LAB, CITY_CLINIC).', defaultVal: 'ALPHANUMERIC' },
      { name: 'PrimaryDomain', desc: 'Access hostname / subdomain (e.g. apex.lims.local, 103.185.75.179).', defaultVal: 'FQDN / Subdomain' },
      { name: 'IsolationModel', desc: 'SeparateDatabase (High Security isolated DB) or SharedPool.', defaultVal: 'SeparateDatabase' },
      { name: 'DatabaseServer & Port', desc: 'Host and MySQL port where tenant schema is provisioned.', defaultVal: '127.0.0.1:3306/3333' }
    ]
  },
  {
    step: 3,
    id: 'step-fleet',
    badge: 'Step 3',
    title: 'Fleet & Tenant Management (टेनेंट फ्लीट और मॉनिटरिंग)',
    shortDesc: 'View all onboarded laboratories, check schema version, rotate database secrets, and view audit history.',
    icon: '🏢',
    route: '/saas/tenants',
    buttonText: 'Open Tenant Fleet Management',
    hindiSummary: 'सभी ऑनबोर्डेड लैब्स की लिस्ट देखें, डेटाबेस कनेक्टिविटी चेक करें और पासवर्ड/सीक्रेट्स रोटेट करें।',
    keyParameters: [
      { name: 'Status', desc: 'ACTIVE (Online), SUSPENDED (Hold), PROVISIONING (Building), or DEACTIVATED.', defaultVal: 'ACTIVE' },
      { name: 'SchemaDrift & DryRun', desc: 'Checks if tenant database schema matches central migration scripts.', defaultVal: 'Verified' },
      { name: 'SecretRotation', desc: 'Rotates AES-256 encrypted database passwords without service downtime.', defaultVal: 'Zero-Downtime' }
    ]
  },
  {
    step: 4,
    id: 'step-centres-staff',
    badge: 'Step 4',
    title: 'Centres, Pathologists & Staff (ब्रांच, लैब डॉक्टर्स और स्टाफ)',
    shortDesc: 'Configure diagnostic collection centres, reporting pathologists with medical council registration, and technicians.',
    icon: '🏥',
    route: '/saas/centres',
    buttonText: 'Open Centres & Clinical Staff',
    hindiSummary: 'लैब की ब्रांच (Collection Centres) और रिपोर्ट अप्रूव करने वाले पैथोलॉजिस्ट डॉक्टर्स और उनके मेडिकल रजिस्ट्रेशन नंबर दर्ज करें।',
    keyParameters: [
      { name: 'CentreCode', desc: 'Unique branch code (e.g. MAIN, DOWNTOWN, BRANCH_02).', defaultVal: 'MAIN' },
      { name: 'MedicalRegNo', desc: 'State Medical Council number printed on signed pathology/radiology reports.', defaultVal: 'MCI/DMC No.' },
      { name: 'Role Assignment', desc: 'Role-based access (Lab Technician, Phlebotomist, Radiologist, Pathologist).', defaultVal: 'Role-Bound' }
    ]
  },
  {
    step: 5,
    id: 'step-wallet-billing',
    badge: 'Step 5',
    title: 'Credits, Wallet & Invoices (क्रेडिट्स, वॉलेट और बिलिंग हिसाब)',
    shortDesc: 'Pre-paid credit balances for notification services, monthly subscription invoices, and payment reconciliation.',
    icon: '💳',
    route: '/saas/wallet',
    buttonText: 'Open Wallet & Invoicing',
    hindiSummary: 'एसएमएस/व्हाट्सएप के लिए वॉलेट क्रेडिट्स टॉप-अप करें, मंथली इनवॉइस देखें और पेमेंट मार्क करें।',
    keyParameters: [
      { name: 'AvailableBalance', desc: 'Current balance of pre-paid operational credits.', defaultVal: 'Token Credits' },
      { name: 'LowBalanceThreshold', desc: 'Credit floor below which automated warning alerts fire.', defaultVal: '100 Credits' },
      { name: 'Invoice Status', desc: 'PENDING, PAID, or OVERDUE with due date enforcement.', defaultVal: 'PAID / OVERDUE' }
    ]
  },
  {
    step: 6,
    id: 'step-lifecycle-quotas',
    badge: 'Step 6',
    title: 'Lifecycle, Quotas & Delinquency Governance (कंट्रोल, कोटा और पेमेंट ड्यू एक्शन)',
    shortDesc: 'Enforce user and monthly registration quotas, set payment overdue actions (Warn vs Soft-Lock vs Hard-Lock), or trigger administrative freeze.',
    icon: '🛡️',
    route: '/saas/lifecycle',
    buttonText: 'Open Lifecycle & Quota Controls',
    hindiSummary: 'यूज़र लिमिट, मंथली रजिस्ट्रेशन लिमिट और अगर पेमेंट ड्यू हो तो क्या एक्शन लेना है (Warn / Soft-Lock / Hard-Lock) तय करें।',
    keyParameters: [
      { name: 'MaxUsers Limit', desc: 'Enforces hard ceiling on created user accounts.', defaultVal: 'Hard Limit' },
      { name: 'MaxRegistrations Limit', desc: 'Monthly maximum patient visit billings permitted.', defaultVal: 'Calendar Month' },
      { name: 'DelinquencyAction: WARN_ONLY', desc: 'Displays amber notification banner; clinical workflows continue normally.', defaultVal: 'Yellow Banner' },
      { name: 'DelinquencyAction: SOFT_LOCK_READONLY', desc: 'Blocks new patient registrations; allows staff to read/print historical reports.', defaultVal: 'Orange Guard' },
      { name: 'DelinquencyAction: HARD_LOCKOUT', desc: 'Completely blocks login with payment settlement screen.', defaultVal: 'Red Freeze' },
      { name: 'AdminLocked', desc: 'Emergency administrative kill-switch invoked by super-administrator.', defaultVal: 'Instant Lock' }
    ]
  }
];

const GLOSSARY_ITEMS = [
  {
    term: 'SeparateDatabase vs SharedPool',
    category: 'Architecture',
    meaningHindi: 'अलग डेटाबेस बनाम शेयर्ड डेटाबेस',
    englishDesc: 'SeparateDatabase creates a dedicated MySQL database for each tenant for strict medical compliance and zero data leakage. SharedPool places multiple smaller clinics in a shared schema separated by TenantID.',
    impact: 'Protects HIPAA/NABL patient privacy with physical or schema-level boundary isolation.'
  },
  {
    term: 'MaxUsers Quota',
    category: 'Quota Control',
    meaningHindi: 'अधिकतम यूज़र अकाउंट सीमा',
    englishDesc: 'Defines how many active employee logins (Pathologists, Radiologists, Lab Technicians, Receptionists) the tenant can maintain.',
    impact: 'Attempting to create user N+1 shows an upgrade prompt.'
  },
  {
    term: 'MaxRegistrationsPerMonth Quota',
    category: 'Quota Control',
    meaningHindi: 'प्रति माह अधिकतम मरीज रजिस्ट्रेशन सीमा',
    englishDesc: 'Maximum patient visits or billing transactions permitted within a single calendar billing period (1st to end of month).',
    impact: 'When reached, receptionists are warned or instructed to upgrade subscription plan.'
  },
  {
    term: 'DelinquencyAction: WARN_ONLY',
    category: 'Billing Policy',
    meaningHindi: 'चेतावनी बैनर (काम चालू रहेगा)',
    englishDesc: 'Displays prominent warning banners across tenant portal when monthly subscription invoices pass due date, without stopping clinical care.',
    impact: 'Zero clinical disruption for patients; billing reminder visible to admins.'
  },
  {
    term: 'DelinquencyAction: SOFT_LOCK_READONLY',
    category: 'Billing Policy',
    meaningHindi: 'सॉफ्ट-लॉक (नया रजिस्ट्रेशन बंद, पुरानी रिपोर्ट देख सकते हैं)',
    englishDesc: 'Gracefully blocks new patient registrations and sample collections while allowing doctors to view, approve, and print existing patient results.',
    impact: 'Ensures patients already in treatment are never harmed while preventing new unpaid liability.'
  },
  {
    term: 'DelinquencyAction: HARD_LOCKOUT',
    category: 'Billing Policy',
    meaningHindi: 'पूर्ण लॉकआउट (लॉगिन बंद)',
    englishDesc: 'Completely blocks tenant logins. Users are redirected to a secure payment settlement page until overdue invoice is reconciled.',
    impact: 'Used for severely overdue accounts (e.g. 30+ days overdue).'
  },
  {
    term: 'AdminLocked (Kill-Switch)',
    category: 'Security',
    meaningHindi: 'प्रशासनिक आपातकालीन लॉक',
    englishDesc: 'Platform Super-Admin kill switch that immediately freezes all access for a tenant across web and APIs with a specified audit reason.',
    impact: 'Instant fail-closed protection in case of security breaches or contract termination.'
  },
  {
    term: 'Envelope Encryption & Key Lifecycle',
    category: 'Security',
    meaningHindi: 'मल्टी-लेयर सुरक्षा और की-रोटेशन',
    englishDesc: 'Every tenant database password and medical attachment is encrypted using a unique Data Encryption Key (DEK) wrapped by a Master Key (KEK).',
    impact: 'Enables compliant key rotation without needing to re-encrypt entire databases.'
  }
];

const SaasGuideNotebook = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [glossarySearch, setGlossarySearch] = useState('');
  const [selectedGlossaryCategory, setSelectedGlossaryCategory] = useState('ALL');

  // Simulator state
  const [simUsers, setSimUsers] = useState(26);
  const [simMaxUsers, setSimMaxUsers] = useState(25);
  const [simRegs, setSimRegs] = useState(1050);
  const [simMaxRegs, setSimMaxRegs] = useState(1000);
  const [simOverdue, setSimOverdue] = useState(true);
  const [simPolicy, setSimPolicy] = useState('SOFT_LOCK_READONLY');

  const navigate = useNavigate();

  // Evaluate simulator verdict
  const isUsersExceeded = simUsers > simMaxUsers;
  const isRegsExceeded = simRegs > simMaxRegs;

  const filteredGlossary = GLOSSARY_ITEMS.filter(item => {
    const matchesCat = selectedGlossaryCategory === 'ALL' || item.category === selectedGlossaryCategory;
    const matchesSearch = !glossarySearch ||
      item.term.toLowerCase().includes(glossarySearch.toLowerCase()) ||
      item.englishDesc.toLowerCase().includes(glossarySearch.toLowerCase()) ||
      item.meaningHindi.toLowerCase().includes(glossarySearch.toLowerCase());
    return matchesCat && matchesSearch;
  });

  return (
    <div className="saas-container">
      {/* Header */}
      <div className="saas-header" style={{ marginBottom: '1.5rem' }}>
        <div className="saas-header-title">
          <h1>
            <span>📘</span> SaaS Platform Operations Playbook &amp; Flowchart
          </h1>
          <p>
            Complete Step-by-Step Lifecycle Guide, Quota Governance, Parameter Glossary &amp; Interactive Simulator
          </p>
        </div>
        <div className="saas-header-actions">
          <button className="saas-btn saas-btn-primary" onClick={() => navigate('/saas/dashboard')}>
            📊 SaaS Dashboard
          </button>
          <button className="saas-btn saas-btn-secondary" onClick={() => navigate('/saas/lifecycle')}>
            🛡️ Lifecycle Controls
          </button>
        </div>
      </div>

      {/* Visual Workflow Flowchart & Lifecycle Stages */}
      <div className="saas-card" style={{ marginBottom: '1.75rem', border: '1px solid #cbd5e1' }}>
        <div className="saas-card-header" style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <div>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>🗺️</span> Step-by-Step SaaS Lifecycle Flowchart (कदम-दर-कदम संचालन क्रम)
            </h3>
            <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>
              Click on any step below to see its exact actions, parameters, and quick-jump link
            </span>
          </div>
          <span className="saas-badge saas-badge-active" style={{ fontSize: '0.78rem' }}>
            ● Recommended Order: Steps 1 → 6
          </span>
        </div>

        <div className="saas-card-body" style={{ padding: '1.5rem' }}>
          {/* Stepper Navigation Strip */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
            {LIFECYCLE_STEPS.map((s) => {
              const isSelected = activeStep === s.step;
              return (
                <div
                  key={s.id}
                  onClick={() => setActiveStep(s.step)}
                  style={{
                    padding: '0.85rem 0.75rem',
                    borderRadius: '8px',
                    border: isSelected ? '2px solid #2563eb' : '1px solid #e2e8f0',
                    background: isSelected ? '#eff6ff' : '#f8fafc',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.15s ease',
                    boxShadow: isSelected ? '0 2px 6px rgba(37,99,235,0.15)' : 'none'
                  }}
                >
                  <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>{s.icon}</div>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: isSelected ? '#1d4ed8' : '#64748b', textTransform: 'uppercase' }}>
                    {s.badge}
                  </span>
                  <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: isSelected ? '#1e293b' : '#334155', marginTop: '0.2rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {s.title.split('(')[0]}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Active Step Detailed Card */}
          {(() => {
            const current = LIFECYCLE_STEPS.find(s => s.step === activeStep) || LIFECYCLE_STEPS[0];
            return (
              <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <span className="saas-badge saas-badge-active" style={{ marginBottom: '0.4rem', display: 'inline-block' }}>
                      {current.badge} Action
                    </span>
                    <h3 style={{ margin: '0.2rem 0', fontSize: '1.25rem', color: '#0f172a' }}>
                      {current.icon} {current.title}
                    </h3>
                    <p style={{ margin: '0.25rem 0 0.5rem 0', color: '#475569', fontSize: '0.9rem' }}>
                      {current.shortDesc}
                    </p>
                    <div style={{ background: '#fef3c7', borderLeft: '4px solid #f59e0b', padding: '0.6rem 0.85rem', borderRadius: '4px', marginTop: '0.5rem' }}>
                      <strong style={{ fontSize: '0.8125rem', color: '#92400e' }}>💡 सरल भाषा में: </strong>
                      <span style={{ fontSize: '0.8125rem', color: '#78350f' }}>{current.hindiSummary}</span>
                    </div>
                  </div>

                  <button
                    className="saas-btn saas-btn-primary"
                    onClick={() => navigate(current.route)}
                    style={{ fontWeight: 600, padding: '0.6rem 1.25rem' }}
                  >
                    🚀 {current.buttonText} →
                  </button>
                </div>

                {/* Key Parameters Table for This Step */}
                <div style={{ marginTop: '1.25rem' }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.875rem', color: '#1e293b' }}>
                    ⚙️ Key Configuration Values in this Step:
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0.75rem' }}>
                    {current.keyParameters.map((p, idx) => (
                      <div key={idx} style={{ background: '#f8fafc', padding: '0.85rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                          <code style={{ fontWeight: 700, color: '#2563eb', fontSize: '0.82rem' }}>{p.name}</code>
                          <span style={{ fontSize: '0.72rem', background: '#e2e8f0', padding: '0.1rem 0.4rem', borderRadius: '4px', fontWeight: 600 }}>
                            {p.defaultVal}
                          </span>
                        </div>
                        <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748b', lineHeight: 1.35 }}>
                          {p.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Interactive Quota & Delinquency Policy Simulator */}
      <div className="saas-card" style={{ marginBottom: '1.75rem', border: '1px solid #cbd5e1' }}>
        <div className="saas-card-header" style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <div>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>🧪</span> Interactive Quota &amp; Overdue Policy Simulator (लाइव नीति सिम्युलेटर)
            </h3>
            <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>
              Test what happens when a client exceeds max users, hits registration limits, or has overdue invoices
            </span>
          </div>
        </div>

        <div className="saas-card-body" style={{ padding: '1.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
            {/* Input Sliders & Controls */}
            <div style={{ background: '#f8fafc', padding: '1.25rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#0f172a' }}>Simulate Tenant Scenario:</h4>

              {/* Users Slider */}
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                  <span>User Accounts: {simUsers}</span>
                  <span style={{ color: isUsersExceeded ? '#dc2626' : '#16a34a' }}>Limit: {simMaxUsers}</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={simUsers}
                  onChange={(e) => setSimUsers(parseInt(e.target.value, 10))}
                  style={{ width: '100%', marginBottom: '0.25rem' }}
                />
                <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center', fontSize: '0.72rem' }}>
                  <span style={{ color: '#64748b' }}>Change Limit:</span>
                  {[10, 25, 50].map(lim => (
                    <button
                      key={lim}
                      type="button"
                      onClick={() => setSimMaxUsers(lim)}
                      style={{
                        padding: '0.1rem 0.4rem',
                        fontSize: '0.7rem',
                        borderRadius: '3px',
                        border: '1px solid #cbd5e1',
                        background: simMaxUsers === lim ? '#2563eb' : '#fff',
                        color: simMaxUsers === lim ? '#fff' : '#475569',
                        cursor: 'pointer'
                      }}
                    >
                      {lim}
                    </button>
                  ))}
                </div>
              </div>

              {/* Registrations Slider */}
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                  <span>Monthly Registrations: {simRegs}</span>
                  <span style={{ color: isRegsExceeded ? '#dc2626' : '#16a34a' }}>Limit: {simMaxRegs}</span>
                </div>
                <input
                  type="range"
                  min="100"
                  max="2000"
                  step="50"
                  value={simRegs}
                  onChange={(e) => setSimRegs(parseInt(e.target.value, 10))}
                  style={{ width: '100%', marginBottom: '0.25rem' }}
                />
                <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center', fontSize: '0.72rem' }}>
                  <span style={{ color: '#64748b' }}>Change Limit:</span>
                  {[500, 1000, 2000].map(lim => (
                    <button
                      key={lim}
                      type="button"
                      onClick={() => setSimMaxRegs(lim)}
                      style={{
                        padding: '0.1rem 0.4rem',
                        fontSize: '0.7rem',
                        borderRadius: '3px',
                        border: '1px solid #cbd5e1',
                        background: simMaxRegs === lim ? '#2563eb' : '#fff',
                        color: simMaxRegs === lim ? '#fff' : '#475569',
                        cursor: 'pointer'
                      }}
                    >
                      {lim}
                    </button>
                  ))}
                </div>
              </div>

              {/* Overdue Checkbox */}
              <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  id="simOverdue"
                  checked={simOverdue}
                  onChange={(e) => setSimOverdue(e.target.checked)}
                />
                <label htmlFor="simOverdue" style={{ fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                  Payment Invoice is Overdue (बकाया पेमेंट)
                </label>
              </div>

              {/* Delinquency Action Select */}
              <div>
                <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.35rem' }}>
                  Enforced Overdue Policy:
                </label>
                <select
                  className="saas-select"
                  value={simPolicy}
                  onChange={(e) => setSimPolicy(e.target.value)}
                  style={{ width: '100%', fontSize: '0.8125rem' }}
                >
                  <option value="WARN_ONLY">🟡 WARN_ONLY (Alert Banners)</option>
                  <option value="SOFT_LOCK_READONLY">🟠 SOFT_LOCK_READONLY (Protected)</option>
                  <option value="HARD_LOCKOUT">🔴 HARD_LOCKOUT (Strict Lock)</option>
                </select>
              </div>
            </div>

            {/* Live Verdict Panel */}
            <div style={{ background: '#fff', padding: '1.25rem', borderRadius: '8px', border: '1px solid #cbd5e1', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div>
                <span className="saas-badge" style={{ background: '#0f172a', color: '#fff', marginBottom: '0.5rem', display: 'inline-block' }}>
                  Live Enforcement Diagnosis
                </span>
                <h4 style={{ margin: '0.25rem 0 0.75rem 0', fontSize: '1rem', color: '#0f172a' }}>
                  What happens in the Tenant LIMS:
                </h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                  {/* User Quota Status */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
                    <span>{isUsersExceeded ? '❌' : '✅'}</span>
                    <span>
                      <strong>Staff Accounts: </strong>
                      {isUsersExceeded ? (
                        <span style={{ color: '#dc2626', fontWeight: 600 }}>Blocked. Cannot create new user (Limit: {simMaxUsers}).</span>
                      ) : (
                        <span style={{ color: '#16a34a' }}>Allowed ({simMaxUsers - simUsers} slots remaining).</span>
                      )}
                    </span>
                  </div>

                  {/* Registration Quota Status */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
                    <span>{isRegsExceeded ? '❌' : '✅'}</span>
                    <span>
                      <strong>Patient Booking: </strong>
                      {isRegsExceeded ? (
                        <span style={{ color: '#dc2626', fontWeight: 600 }}>Blocked. Monthly quota exceeded (Limit: {simMaxRegs}).</span>
                      ) : (
                        <span style={{ color: '#16a34a' }}>Allowed ({simMaxRegs - simRegs} bookings remaining).</span>
                      )}
                    </span>
                  </div>

                  {/* Payment Overdue Policy Status */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
                    <span>{simOverdue ? (simPolicy === 'HARD_LOCKOUT' ? '⛔' : (simPolicy === 'SOFT_LOCK_READONLY' ? '⚠️' : '📢')) : '✅'}</span>
                    <span>
                      <strong>Payment Action: </strong>
                      {!simOverdue ? (
                        <span style={{ color: '#16a34a' }}>Account in Good Standing (Current).</span>
                      ) : simPolicy === 'HARD_LOCKOUT' ? (
                        <span style={{ color: '#dc2626', fontWeight: 700 }}>LOGIN BLOCKED. Tenant users locked out until invoice settlement.</span>
                      ) : simPolicy === 'SOFT_LOCK_READONLY' ? (
                        <span style={{ color: '#ea580c', fontWeight: 600 }}>SOFT LOCK. New bookings blocked; report viewing &amp; printing allowed.</span>
                      ) : (
                        <span style={{ color: '#ca8a04', fontWeight: 600 }}>WARN ONLY. Alert banner shown; all clinical operations continue.</span>
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                  Need to apply these rules to a live tenant?
                </span>
                <button className="saas-btn saas-btn-primary" onClick={() => navigate('/saas/lifecycle')} style={{ fontSize: '0.8125rem' }}>
                  Go to Lifecycle &amp; Controls →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Parameter Glossary & Runbook */}
      <div className="saas-card" style={{ marginBottom: '1.75rem', border: '1px solid #cbd5e1' }}>
        <div className="saas-card-header" style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <div>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>📖</span> Configuration Field Glossary &amp; Terms (शब्दावली और मतलब)
            </h3>
            <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>
              Clear explanations of every configuration field in both Hindi and English
            </span>
          </div>

          {/* Category filter */}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {['ALL', 'Architecture', 'Quota Control', 'Billing Policy', 'Security'].map(cat => (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedGlossaryCategory(cat)}
                style={{
                  padding: '0.25rem 0.65rem',
                  fontSize: '0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #cbd5e1',
                  background: selectedGlossaryCategory === cat ? '#2563eb' : '#fff',
                  color: selectedGlossaryCategory === cat ? '#fff' : '#475569',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        <div className="saas-card-body" style={{ padding: '1.5rem' }}>
          {/* Search bar */}
          <div style={{ marginBottom: '1.25rem' }}>
            <input
              type="text"
              className="saas-input"
              placeholder="Search terms (e.g. MaxUsers, Delinquency, Encryption, Database)..."
              value={glossarySearch}
              onChange={(e) => setGlossarySearch(e.target.value)}
              style={{ width: '100%', maxWidth: '400px', fontSize: '0.85rem' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1rem' }}>
            {filteredGlossary.map((item, idx) => (
              <div key={idx} style={{ background: '#f8fafc', padding: '1.25rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                  <code style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1e293b' }}>{item.term}</code>
                  <span className="saas-badge" style={{ background: '#e2e8f0', color: '#334155', fontSize: '0.7rem' }}>
                    {item.category}
                  </span>
                </div>
                <div style={{ color: '#047857', fontWeight: 600, fontSize: '0.8125rem', marginBottom: '0.4rem' }}>
                  🇮🇳 {item.meaningHindi}
                </div>
                <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.8125rem', color: '#475569', lineHeight: 1.4 }}>
                  {item.englishDesc}
                </p>
                <div style={{ fontSize: '0.75rem', color: '#64748b', borderTop: '1px dashed #cbd5e1', paddingTop: '0.4rem' }}>
                  <strong>Impact: </strong>{item.impact}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaasGuideNotebook;
